import { effect, inject, Injectable, untracked } from '@angular/core';

import {
  TellentAssistantControlService,
  TellentAssistantService,
} from '@core/tellent-assistant-logic';
import type { AssistantModalHandle } from '@core/tellent-assistant-mobile-contracts';
import {
  injectTellentAssistantMobileCompanyKey,
  injectTellentAssistantMobileModalPresenter,
  injectTellentAssistantMobileTranslationSources,
} from '@core/tellent-assistant-mobile-contracts';

import { TellentAssistantMobileI18nService } from './assistant-i18n.providers';

@Injectable({ providedIn: 'root' })
export class TellentAssistantMobileService {
  private readonly control = inject(TellentAssistantControlService);
  private readonly assistant = inject(TellentAssistantService);
  private readonly i18n = inject(TellentAssistantMobileI18nService);
  private readonly presenter = injectTellentAssistantMobileModalPresenter();
  private readonly companyKey = injectTellentAssistantMobileCompanyKey();
  private readonly translationSources = injectTellentAssistantMobileTranslationSources();

  private readonly presentOnOpen = effect(() => {
    const open = this.control.open();
    untracked(
      () =>
        void (open
          ? this.present().catch(error => {
              console.error('Tellent assistant failed to open:', error);
              this.control.open.set(false);
            })
          : this.dismiss()),
    );
  });

  private readonly resetOnCompanyChange = effect(() => {
    const key = this.companyKey();
    untracked(() => {
      if (this.lastCompanyKey != null && this.lastCompanyKey !== key) {
        this.control.close();
        this.assistant.resetThread();
      }
      this.lastCompanyKey = key;
    });
  });

  private modal: AssistantModalHandle | null = null;
  private presenting = false;
  private lastCompanyKey: string | null | undefined;

  public toggle(): void {
    this.control.toggle();
  }

  private async present(): Promise<void> {
    if (this.modal || this.presenting) return;
    this.presenting = true;
    try {
      await this.presentModal();
    } finally {
      this.presenting = false;
    }
    // open() may have flipped back to true while we were presenting; the
    // effect saw presenting=true and won't re-fire, so reconcile here.
    if (this.control.open() && !this.modal) await this.present();
  }

  private async presentModal(): Promise<void> {
    await this.i18n.load(this.translationSources);
    const { TellentAssistantMobileModalComponent } =
      await import('./tellent-assistant-mobile-modal/tellent-assistant-mobile-modal.component');
    if (!this.control.open()) return;
    const modal = await this.presenter.createCard({
      component: TellentAssistantMobileModalComponent,
      canDismiss: true,
    });
    void modal.onWillDismiss().then(() => {
      if (this.modal !== modal) return;
      this.modal = null;
      if (this.control.open()) this.control.open.set(false);
    });
    // dismiss() is a no-op before present(), so a bail must present first
    // or the created ion-modal element leaks.
    await modal.present();
    if (!this.control.open()) {
      await modal.dismiss();
      return;
    }
    this.modal = modal;
  }

  private async dismiss(): Promise<void> {
    const modal = this.modal;
    this.modal = null;
    await modal?.dismiss();
  }
}
